import { STORAGE_KEYS } from '../../constants/storage';

const API_URL = process.env.REACT_APP_API_URL || '';

export interface TrialEligibilityResponse {
  eligible: boolean;
  reason: string;
  organization_id: number;
  current_license_type: string | null;
  chat_assistant_enabled: boolean;
}

export interface TrialActivationResponse {
  success: boolean;
  message: string;
  organization_id: number;
  trial_start_date: string;
  trial_end_date: string;
  trial_duration_days: number;
}

export interface TrialStatusResponse {
  organization_id: number;
  has_access: boolean;
  reason: string;
  license_type: string | null;
  is_trial: boolean;
  trial_days_remaining: number | null;
  trial_expired: boolean;
  chat_assistant_enabled: boolean;
}

export interface TrialActivationError {
  success: false;
  error: string;
  detail?: string;
}

export interface TrialEnsureResponse {
  status: 'activated' | 'already_active' | 'expired' | 'not_eligible';
  has_access: boolean;
  is_trial: boolean;
  trial_days_remaining?: number;
  license_type?: string | null;
}

export interface UsageStatsResponse {
  organization_id: number;
  conversation_count: number;
  estimated_time_saved_minutes: number;
  estimated_time_saved_hours: number;
  trial_start_date: string | null;
  trial_end_date: string | null;
}

const getAuthToken = (): string | null => {
  const authTokenRaw = document.cookie
    .split('; ')
    .find(c => c.startsWith('auth_token='))?.split('=')[1] 
    || localStorage.getItem('auth_token');
  
  if (authTokenRaw) {
    try {
      const parsedToken = JSON.parse(authTokenRaw);
      return parsedToken.access_token || null;
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Check if an organization is eligible to start a trial
 */
export const checkTrialEligibility = async (
  organizationId: string
): Promise<TrialEligibilityResponse> => {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(
    `${API_URL}/api/v1/trial/eligibility/${organizationId}`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to check trial eligibility: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get the current trial/license status for an organization
 */
export const getTrialStatus = async (
  organizationId: string
): Promise<TrialStatusResponse> => {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(
    `${API_URL}/api/v1/trial/status/${organizationId}`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to get trial status: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Silently ensure a trial is active for an organization.
 * Idempotent - safe to call multiple times.
 * Used for auto-activation without user interaction.
 */
export const ensureTrial = async (
  organizationId: string
): Promise<TrialEnsureResponse> => {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(
    `${API_URL}/api/v1/trial/ensure`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        organization_id: parseInt(organizationId, 10),
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to ensure trial: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Activate a trial for an organization
 */
export const activateTrial = async (
  organizationId: string
): Promise<TrialActivationResponse> => {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(
    `${API_URL}/api/v1/trial/activate`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        organization_id: parseInt(organizationId, 10),
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(errorData.detail || `Failed to activate trial: ${response.statusText}`);
    (error as any).response = errorData;
    throw error;
  }

  return response.json();
};

/**
 * Get usage statistics for a trial organization
 */
export const getUsageStats = async (
  organizationId: string
): Promise<UsageStatsResponse> => {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(
    `${API_URL}/api/v1/trial/usage/${organizationId}`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to get usage stats: ${response.statusText}`);
  }

  return response.json();
};

export interface TrialFeedbackRequest {
  organization_id: number;
  rating: number;
  comment?: string;
  conversation_count?: number;
  estimated_time_saved_hours?: number;
}

/**
 * Submit feedback for an expired trial
 */
export const submitTrialFeedback = async (
  feedback: TrialFeedbackRequest
): Promise<void> => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(
    `${API_URL}/api/v1/trial/feedback`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(feedback),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to submit feedback: ${response.statusText}`);
  }
};

/**
 * Report a trial activation error to the backend (similar to feedback submission)
 */
export const reportTrialError = async (
  organizationId: string,
  errorMessage: string,
  errorDetails?: string
): Promise<void> => {
  const token = getAuthToken();
  
  if (!token) {
    console.error('Cannot report trial error: No authentication token');
    return;
  }

  try {
    await fetch(`${API_URL}/api/v1/feedback`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        trace_id: `trial-error-${Date.now()}`,
        session_id: localStorage.getItem(STORAGE_KEYS.SESSION_ID) || 'unknown',
        score: 0,
        comment: `Trial activation error for org ${organizationId}: ${errorMessage}`,
        ai_answer: errorDetails || 'No additional details',
        question: 'Trial activation attempt',
      }),
    });
  } catch (e) {
    console.error('Failed to report trial error:', e);
  }
};

